import { useEffect, useMemo } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { usePathname } from "next/navigation";
import { useGSAP } from "@gsap/react";

// Register plugins once
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, SplitText);
}

export const splitLeft = () => {
  const pathName = usePathname();

  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth <= 767) return;

    const splitItems = gsap.utils.toArray(".split-left");
    if (splitItems.length === 0) return;

    const animations = splitItems.map((item) => {
      try {
        const parentSplit = new SplitText(item, { linesClass: "split-parent" });
        new SplitText(item, { type: "lines, chars", linesClass: "split-child" });

        return gsap.from(parentSplit.lines, {
          duration: 1,
          yPercent: 100,
          scrollTrigger: {
            trigger: item,
            toggleActions: 'play none none none',
          },
        });
      } catch (error) {
        console.error("Error while splitting text or creating animation:", error);
        return null;
      }
    });

    ScrollTrigger.refresh();

    return () => {
      animations.forEach(anim => anim?.scrollTrigger?.kill());
    };
  }, [pathName]);
};

export const ParallaxXX = () => {
  const pathName = usePathname();

  useGSAP(() => {
    if (typeof window === "undefined") return;

    const parallaxItems = gsap.utils.toArray(".parallax-x");
    if (parallaxItems.length === 0) return;

    const animations = parallaxItems.map((item) => {
      const parallaxSize = item.getAttribute("data-size") || -40;
      return gsap.to(item, {
        xPercent: parallaxSize,
        ease: "none",
        scrollTrigger: {
          trigger: item,
          scrub: true,
        },
      });
    });

    ScrollTrigger.refresh();

    return () => {
      animations.forEach(anim => anim?.scrollTrigger?.kill());
    };
  }, [pathName]);
};

export const ParallaxX = () => {
  const pathName = usePathname();

  useGSAP(() => {
    if (typeof window === "undefined") return;

    const animations = gsap.utils.toArray(".parallax-xx").map((item) => {
      const parallaxSize = item.getAttribute("data-size") || -20;
      return gsap.to(item, {
        xPercent: parallaxSize,
        duration: 1,
        ease: "none",
        scrollTrigger: {
          trigger: item,
          scrub: true,
        },
      });
    });

    return () => {
      animations.forEach(anim => anim?.scrollTrigger?.kill());
    };
  }, [pathName]);
};

export const ParallaxXR = () => {
  const pathName = usePathname();

  useGSAP(() => {
    if (typeof window === "undefined") return;

    const animations = gsap.utils.toArray(".parallax-xr").map((item) => {
      const parallaxSize = item.getAttribute("data-size") || 30;
      return gsap.to(item, {
        xPercent: parallaxSize,
        ease: "none",
        scrollTrigger: {
          trigger: item,
          scrub: true,
        },
      });
    });

    return () => {
      animations.forEach(anim => anim?.scrollTrigger?.kill());
    };
  }, [pathName]);
};

export const FadeUp = () => {
  const pathName = usePathname();

  useGSAP(() => {
    if (typeof window === "undefined") return;

    const animations = gsap.utils.toArray(".fade-up-total").map((item) => {
      return gsap.from(item, {
        y: "50px",
        ease: "power4.out",
        duration: 1,
        delay: 0.01,
        scrollTrigger: {
          trigger: item,
          toggleActions: "restart none none reset",
        },
      });
    });

    return () => {
      animations.forEach(anim => anim?.scrollTrigger?.kill());
    };
  }, [pathName]);
};

export const SplitUp = () => {
  const pathName = usePathname();

  useGSAP(() => {
    if (typeof window === "undefined" || window.innerWidth <= 767) return;

    const animations = gsap.utils.toArray('.split-up').map((item) => {
      try {
        const parentSplit = new SplitText(item, { linesClass: "split-parent" });
        const childSplit = new SplitText(item, { type: "lines, chars", linesClass: "split-child" });

        childSplit.chars.forEach((char) => {
          char.style.letterSpacing = window.getComputedStyle(item).letterSpacing;
          char.style.whiteSpace = 'nowrap';
        });

        return gsap.from(parentSplit.lines, {
          duration: 1,
          yPercent: 100,
          scrollTrigger: {
            trigger: item,
            toggleActions: 'play none none none',
          }
        });
      } catch (error) {
        console.error("Error while splitting text or creating animation:", error);
        return null;
      }
    });

    ScrollTrigger.refresh();

    return () => {
      animations.forEach(anim => anim?.scrollTrigger?.kill());
    };
  }, [pathName]);
};
